import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  Link,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  LocationOn,
  CheckCircle,
  Error as ErrorIcon,
  Refresh,
  Map,
  GpsFixed,
  GpsNotFixed,
  Lock,
  LockOpen
} from '@mui/icons-material';
import geofencingService from '../../services/geofencingService';

const GeofenceVerification = ({ 
  siteAddress,
  allowedRadius = 100,
  onVerificationChange,
  autoVerify = true,
  showMap = true,
  required = true
}) => {
  const [checking, setChecking] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState('');
  const [permissionState, setPermissionState] = useState('unknown');

  useEffect(() => {
    checkPermission();
    if (autoVerify && siteAddress) {
      verifyLocation();
    }
  }, []);

  const checkPermission = async () => {
    try {
      const state = await geofencingService.requestPermission();
      setPermissionState(state);
    } catch (err) {
      console.error('Permission check error:', err);
      setPermissionState('denied');
    }
  };

  const verifyLocation = async () => {
    if (!siteAddress || !siteAddress.latitude || !siteAddress.longitude) {
      setError('Site coordinates are not available');
      return;
    }

    setChecking(true);
    setError('');

    try {
      const result = await geofencingService.verifyOnSite(siteAddress, allowedRadius);
      
      setVerificationResult(result);
      setVerified(result.verified);

      if (onVerificationChange) {
        onVerificationChange(result);
      }

      if (!result.verified) {
        setError(result.message);
      }
    } catch (err) {
      console.error('Location verification error:', err);
      setError(err.message || 'Failed to verify location');
      setVerified(false);
      
      if (onVerificationChange) {
        onVerificationChange({ verified: false, error: err.message });
      }
    } finally {
      setChecking(false);
    }
  };

  const getStatusColor = () => {
    if (checking) return 'info';
    if (verified) return 'success';
    if (error) return 'error';
    return 'warning';
  };

  const getStatusIcon = () => {
    if (checking) return <GpsNotFixed />;
    if (verified) return <CheckCircle />;
    if (error) return <ErrorIcon />;
    return <LocationOn />;
  };

  const getStatusText = () => {
    if (checking) return 'Verifying location...';
    if (verified) return 'Location verified - You are at the site';
    if (error) return 'Location verification failed';
    return 'Location not verified';
  };

  const getDistanceColor = () => {
    if (!verificationResult) return 'default';
    const { distance, allowedRadius } = verificationResult;
    if (distance <= allowedRadius) return 'success';
    if (distance <= allowedRadius * 2) return 'warning';
    return 'error';
  };

  const formatDistance = (meters) => {
    if (meters < 1000) {
      return `${meters}m`;
    }
    return `${(meters / 1000).toFixed(1)}km`;
  };

  if (!geofencingService.isSupported()) {
    return (
      <Alert severity="error">
        Geolocation is not supported by your browser. You cannot write inspection reports without location verification.
      </Alert>
    );
  }

  if (permissionState === 'denied') {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={checkPermission}>
          Retry
        </Button>
      }>
        Location permission denied. Please enable location access in your browser settings to write inspection reports.
      </Alert>
    );
  }

  return (
    <Paper 
      sx={{ 
        p: 2, 
        border: 2,
        borderColor: verified ? 'success.main' : error ? 'error.main' : 'warning.main',
        bgcolor: verified ? 'success.50' : error ? 'error.50' : 'warning.50'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getStatusIcon()}
          <Typography variant="subtitle2" fontWeight="bold">
            On-Site Verification
          </Typography>
          {required && (
            <Chip 
              label="REQUIRED" 
              size="small" 
              color="error"
              variant="outlined"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {verified ? (
            <LockOpen color="success" />
          ) : (
            <Lock color="error" />
          )}
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {verified 
          ? 'You are verified to be at the inspection site. You can now write your report.'
          : 'You must be physically present at the inspection site to write a report. Your location will be recorded.'}
      </Typography>

      {checking && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            Getting your location...
          </Typography>
        </Box>
      )}

      {error && !checking && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {verificationResult && !checking && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Distance from site:
            </Typography>
            <Chip 
              label={formatDistance(verificationResult.distance)}
              size="small"
              color={getDistanceColor()}
              icon={<LocationOn />}
            />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Allowed radius:
            </Typography>
            <Typography variant="caption" fontWeight="bold">
              {formatDistance(verificationResult.allowedRadius)}
            </Typography>
          </Box>

          {verificationResult.accuracy && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Location accuracy:
              </Typography>
              <Typography variant="caption" fontWeight="bold">
                ±{formatDistance(verificationResult.accuracy)}
              </Typography>
            </Box>
          )}

          {verificationResult.position && showMap && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link
                href={geofencingService.getMapLink(
                  verificationResult.position.latitude,
                  verificationResult.position.longitude
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="small"
                  startIcon={<Map />}
                  variant="outlined"
                >
                  View on Map
                </Button>
              </Link>
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant={verified ? 'outlined' : 'contained'}
          onClick={verifyLocation}
          disabled={checking}
          startIcon={checking ? <CircularProgress size={16} /> : <GpsFixed />}
          fullWidth
          color={verified ? 'success' : 'primary'}
        >
          {checking ? 'Verifying...' : verified ? 'Re-verify Location' : 'Verify Location'}
        </Button>
      </Box>

      {verified && (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            Report writing is now enabled
          </Typography>
          <Typography variant="caption">
            Your location has been verified and will be recorded with your report.
          </Typography>
        </Alert>
      )}

      {!verified && !checking && verificationResult && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            Report writing is locked
          </Typography>
          <Typography variant="caption">
            Move closer to the site and verify your location to unlock report writing.
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default GeofenceVerification;
